import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Button, FilterableMultiSelect, InlineNotification, Modal, Pagination, Select, SelectItem, Tag, TextArea, TextInput } from '@carbon/react';
import type { ColumnDef } from '@tanstack/react-table';
import { Link } from 'react-router';
import { api } from '../api/client';
import { DataTable } from '../components/DataTable';
import { InlineError, PageHeader } from '../components/states';
import { fromZoneInput, toZoneInput } from '@ruby-cms/shared';
import { CURRENCY, Money, When } from '../components/format';
import { useAuth } from '../auth/AuthContext';
import { SegmentSelect, useSegments } from './Segments';
import { blankForm, CHU_KY, DIEU_KIEN, digits, formOf, LOAI, openCheckin, ruleOf, summaryOf, unitOf, type QuestForm, type Rule } from './campaignForm';

type Camp = {
  id: string; title: string; kind: string; active: boolean; startsAt: string; endsAt: string; reward: string; body: string; rule?: Rule | null;
  budget?: string | null; claims: number; paid: string; holdoutPct?: number; segmentId?: string | null;
};
type Claim = { id: string; gameUserId: number; nickname: string | null; period: string; amount: string; status: string; createdAt: string; commandKey: string };
type Claims = { campaign: { title: string; budget?: string | null; claims: number; paid: string }; items: Claim[]; total: number; page: number; pageSize: number };
const CLAIM_STATUS: Record<string, [string, 'green' | 'blue' | 'red']> = { DONE: ['Đã cộng', 'green'], PENDING: ['Đang cộng', 'blue'], FAILED: ['Lỗi, không cộng', 'red'] };
const claimColumns: ColumnDef<Claim, any>[] = [
  { header: 'Lúc', accessorKey: 'createdAt', cell: (c) => <When v={c.getValue()} /> },
  { header: 'Người chơi', id: 'u', accessorFn: (r) => r.nickname ?? '#' + r.gameUserId,
    cell: (c) => <Link className="cell-link" to={`/users/${c.row.original.gameUserId}`}>{c.getValue()}</Link> },
  { header: 'Kỳ', accessorKey: 'period' },
  { header: 'Thưởng', accessorKey: 'amount', cell: (c) => <Money v={c.getValue()} /> },
  { header: 'Trạng thái', accessorKey: 'status', cell: (c) => {
    const [label, type] = CLAIM_STATUS[c.getValue()] ?? [c.getValue(), 'blue'];
    return <Tag size="sm" type={type}>{label}</Tag>;
  } },
];

type Page = { items: Camp[] };
type Opt = { value: string; label: string };

const columns: ColumnDef<Camp, any>[] = [
  { header: 'Tiêu đề', accessorKey: 'title' },
  { header: 'Loại', accessorKey: 'kind', cell: (c) => c.getValue() === 'QUEST' ? 'Nhiệm vụ' : 'Sự kiện' },
  { header: 'Thưởng tối đa / kỳ', accessorKey: 'reward', cell: (c) => <Money v={c.getValue()} /> },
  { header: 'Loại nhiệm vụ', id: 'loai', cell: (c) => LOAI.find(([k]) => k === (c.row.original.rule?.loai ?? 'cuoc'))?.[1] ?? '' },
  { header: 'Điều kiện', id: 'rule', cell: (c) => summaryOf(c.row.original.rule, c.row.original.reward) },
  { header: 'Bắt đầu', accessorKey: 'startsAt', cell: (c) => <When v={c.getValue()} /> },
  { header: 'Kết thúc', accessorKey: 'endsAt', cell: (c) => <When v={c.getValue()} /> },
  { header: 'Đã trả', id: 'paid', cell: (c) => <><Money v={c.row.original.paid} /> ({c.row.original.claims} lượt)</> },
  { header: 'Ngân sách', accessorKey: 'budget', cell: (c) => c.getValue() ? <Money v={c.getValue()} /> : 'Không giới hạn' },
  { header: 'Nhóm đối chứng', accessorKey: 'holdoutPct', cell: (c) => c.getValue() ? <span className="num">{c.getValue()}%</span> : '-' },
  { header: 'Bật', accessorKey: 'active', cell: (c) => c.getValue() ? 'có' : 'tắt' },
];

// datetime-local value in the CMS time zone
function isoLocal(d: Date) {
  return toZoneInput(d);
}

const localOf = (iso: string) => isoLocal(new Date(iso));

export function CampaignsPage() {
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ['campaigns'], queryFn: () => api.get<Page>('/api/admin/campaigns') });
  const done = () => qc.invalidateQueries({ queryKey: ['campaigns'] });

  // the form: create (editing = null) or edit a campaign
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState<Camp | null>(null);
  const [title, setTitle] = useState('');
  const [kind, setKind] = useState('EVENT');
  const [body, setBody] = useState('');
  const [reward, setReward] = useState('0');
  const [budget, setBudget] = useState('');
  const [startsAt, setStartsAt] = useState(isoLocal(new Date()));
  const [endsAt, setEndsAt] = useState(isoLocal(new Date(Date.now() + 7 * 86400_000)));
  const [form, setForm] = useState<QuestForm>(blankForm());
  const [holdout, setHoldout] = useState('0');
  const [seg, setSeg] = useState('');
  const { can } = useAuth();
  const segs = useSegments(can('crm.manage'));
  const segName = (id: string) => segs.data?.items.find((x) => x.id === id)?.name ?? '(đã xóa hoặc không có quyền xem)';
  const set = (patch: Partial<QuestForm>) => setForm((f) => ({ ...f, ...patch }));
  const startCreate = () => {
    setEditing(null); setTitle(''); setKind('QUEST'); setBody(''); setReward('0'); setBudget(''); setForm(blankForm()); setHoldout('0'); setSeg('');
    setStartsAt(isoLocal(new Date())); setEndsAt(isoLocal(new Date(Date.now() + 7 * 86400_000))); setOpen(true);
  };
  const startEdit = (c: Camp) => {
    setEditing(c); setTitle(c.title); setKind(c.kind); setBody(c.body); setReward(String(c.reward)); setBudget(c.budget ? String(c.budget) : '');
    setForm(formOf(c.rule, String(c.reward))); setSeg(c.segmentId ?? '');
    setStartsAt(localOf(c.startsAt)); setEndsAt(localOf(c.endsAt)); setPick(null); setOpen(true);
  };
  const games = useQuery({ queryKey: ['campaign-games'], queryFn: () => api.get<{ ledger: Opt[]; rounds: Opt[] }>('/api/admin/campaigns/games'), enabled: open });
  const built = ruleOf(form);
  const save = useMutation({
    mutationFn: () => {
      // a tiered or check-in rule carries its rewards; the server stores what one period can pay
      const b = {
        title, kind, body, reward: '0', budget: budget ? budget : null, segmentId: seg || null, startsAt: fromZoneInput(startsAt)?.toISOString(), endsAt: fromZoneInput(endsAt)?.toISOString(),
        rule: built.rule,
      };
      // the control group share is fixed at creation
      return editing ? api.patch('/api/admin/campaigns/' + editing.id, b) : api.post('/api/admin/campaigns', { ...b, holdoutPct: Number(holdout || 0) });
    },
    onSuccess: () => { setOpen(false); done(); },
  });

  // one campaign: switch on/off, delete, its claims
  const [pick, setPick] = useState<Camp | null>(null);
  const [page, setPage] = useState({ page: 1, pageSize: 25 });
  const claims = useQuery({
    queryKey: ['campaign-claims', pick?.id, page],
    queryFn: () => api.get<Claims>(`/api/admin/campaigns/${pick!.id}/claims`, page),
    enabled: !!pick,
  });
  const bat = useMutation({
    mutationFn: () => api.patch('/api/admin/campaigns/' + pick!.id, { active: !pick!.active }),
    onSuccess: () => { setPick(null); done(); },
  });
  const [confirmDel, setConfirmDel] = useState(false);
  const del = useMutation({
    mutationFn: () => api.del('/api/admin/campaigns/' + pick!.id),
    onSuccess: () => { setConfirmDel(false); setPick(null); done(); },
  });

  return (
    <div className="page">
      <PageHeader title="Sự kiện / nhiệm vụ" lead="Hiện trong popup Nhiệm vụ của game. Nhiệm vụ cược, nạp, số ván, số ván thắng có nhiều mốc thưởng theo ngày, tuần, tháng hoặc cả đợt; điểm danh trả thưởng theo từng ngày của chuỗi. Mỗi mốc / mỗi ngày nhận một lần, không vượt ngân sách."
        actions={<Button size="sm" onClick={startCreate}>Tạo</Button>} />
      <DataTable columns={columns} data={q.data?.items} loading={q.isLoading} error={q.error} onRetry={() => q.refetch()}
        onRowClick={(r) => { setPick(r); setPage({ page: 1, pageSize: 25 }); }} rowKey={(r) => r.id}
        empty={{ title: 'Chưa có chiến dịch', body: 'Tạo sự kiện hoặc nhiệm vụ để hiện khi client gọi API.' }} />

      <Modal open={!!pick && !confirmDel} size="lg" modalHeading={pick?.title ?? ''} passiveModal onRequestClose={() => setPick(null)}>
        {pick ? (
          <div className="stack">
            <p>{summaryOf(pick.rule, pick.reward)} · tối đa <Money v={pick.reward} /> một kỳ · {pick.budget ? <>ngân sách <Money v={pick.budget} /></> : 'không giới hạn ngân sách'}</p>
            {pick.segmentId ? <p>Chỉ người chơi thuộc phân khúc {segName(pick.segmentId)} thấy và nhận được.</p> : null}
            {pick.holdoutPct ? <p>{pick.holdoutPct}% người chơi thuộc nhóm đối chứng, không thấy chiến dịch này.</p> : null}
            <p>{pick.active ? 'Đang hiện cho người chơi. Tắt thì nhiệm vụ biến khỏi game, thưởng đã nhận giữ nguyên.' : 'Đang tắt.'}</p>
            <div className="row">
              <Button size="sm" kind="tertiary" onClick={() => startEdit(pick)}>Sửa</Button>
              <Button size="sm" kind="secondary" disabled={bat.isPending} onClick={() => bat.mutate()}>{pick.active ? 'Tắt' : 'Bật'}</Button>
              <Button size="sm" kind="danger--tertiary" onClick={() => setConfirmDel(true)}>Xóa</Button>
            </div>
            <InlineError error={bat.error} />
            <p className="cds--label">Lượt nhận: {claims.data ? <>{claims.data.campaign.claims} lượt, đã trả <Money v={claims.data.campaign.paid} /></> : '...'}</p>
            <DataTable columns={claimColumns} data={claims.data?.items} loading={claims.isLoading} error={claims.error} onRetry={() => claims.refetch()} rowKey={(r) => r.id}
              empty={{ title: 'Chưa ai nhận thưởng', body: 'Người chơi đạt điều kiện rồi bấm nhận trong game.' }} />
            {claims.data && claims.data.total > page.pageSize ? (
              <Pagination page={page.page} pageSize={page.pageSize} pageSizes={[25, 50, 100]} totalItems={claims.data.total}
                itemsPerPageText="Mỗi trang" onChange={({ page: p, pageSize }) => setPage({ page: p, pageSize })} />
            ) : null}
          </div>
        ) : null}
      </Modal>

      <Modal open={confirmDel} danger modalHeading={'Xóa ' + (pick?.title ?? '')} primaryButtonText="Xóa" secondaryButtonText="Hủy"
        primaryButtonDisabled={del.isPending} onRequestClose={() => setConfirmDel(false)} onRequestSubmit={() => del.mutate()}>
        <p>Chiến dịch biến khỏi game và danh sách. Thưởng đã trả và danh sách lượt nhận vẫn được giữ.</p>
        <InlineError error={del.error} />
      </Modal>

      <Modal open={open} modalHeading={editing ? 'Sửa ' + editing.title : 'Tạo chiến dịch'} primaryButtonText={editing ? 'Lưu' : 'Tạo'} secondaryButtonText="Hủy"
        size="lg" primaryButtonDisabled={!title || !body || !!built.error || (!editing && Number(holdout) > 50) || save.isPending}
        onRequestClose={() => setOpen(false)} onRequestSubmit={() => save.mutate()}>
        <div className="stack">
          <TextInput id="ca-t" labelText="Tiêu đề" value={title} onChange={(e) => setTitle(e.target.value)} />
          <Select id="ca-k" labelText="Hiện ở" value={kind} onChange={(e) => setKind(e.target.value)}>
            <SelectItem value="QUEST" text="Nhiệm vụ" />
            <SelectItem value="EVENT" text="Sự kiện" />
          </Select>
          <TextArea id="ca-b" labelText="Nội dung" value={body} onChange={(e) => setBody(e.target.value)} rows={3} />
          <Select id="ca-l" labelText="Loại nhiệm vụ" value={form.loai} disabled={!!editing && editing.claims > 0}
            helperText={editing && editing.claims > 0 ? 'Đã có người nhận thưởng: không đổi loại, chu kỳ, các mốc hay số ngày được; thưởng và điều kiện vẫn sửa được.' : undefined}
            onChange={(e) => set({ loai: e.target.value as QuestForm['loai'], games: [] })}>
            {LOAI.map(([v, t]) => <SelectItem key={v} value={v} text={t} />)}
          </Select>

          {form.loai === 'diem_danh' ? (
            <>
              <p className="cds--label">Mỗi ngày người chơi bấm nhận một lần. Ngày 1, 2, 3... theo chuỗi liên tiếp; thưởng và điều kiện của từng ngày đặt ở bảng dưới.</p>
              <div className="row">
                <Select id="ca-dk" labelText="Điều kiện mặc định mỗi ngày" value={form.dieuKien} onChange={(e) => set({ dieuKien: e.target.value as QuestForm['dieuKien'] })}>
                  {DIEU_KIEN.map(([v, t]) => <SelectItem key={v} value={v} text={t} />)}
                </Select>
                {form.dieuKien !== 'khong' ? (
                  <TextInput id="ca-mn" labelText={`Mức ${form.dieuKien === 'nap' ? 'nạp' : 'cược'} mặc định (${CURRENCY.symbol})`} value={form.mucNgay}
                    onChange={(e) => set({ mucNgay: digits(e.target.value) })} />
                ) : null}
                <TextInput id="ca-sn" labelText="Số ngày (1 đến 31)" inputMode="numeric" value={String(form.ngay.length)} disabled={!!editing && editing.claims > 0}
                  onChange={(e) => {
                    const n = Math.min(31, Math.max(1, Number(digits(e.target.value)) || 1));
                    set({ ngay: Array.from({ length: n }, (_, i) => form.ngay[i] ?? { thuong: form.ngay[form.ngay.length - 1]?.thuong ?? '', dieuKien: '', muc: '' }) });
                  }} />
              </div>
              <table className="plain ngay">
                <thead><tr><th>Ngày</th><th>Thưởng ({CURRENCY.symbol})</th><th>Điều kiện riêng</th><th>Mức riêng ({CURRENCY.symbol})</th></tr></thead>
                <tbody>
                  {form.ngay.map((d, i) => {
                    const patch = (x: Partial<typeof d>) => set({ ngay: form.ngay.map((y, j) => (j === i ? { ...y, ...x } : y)) });
                    return (
                      <tr key={i}>
                        <td className="num">{i + 1}</td>
                        <td><TextInput id={'ca-dt' + i} size="sm" hideLabel labelText={'Thưởng ngày ' + (i + 1)} value={d.thuong} onChange={(e) => patch({ thuong: digits(e.target.value) })} /></td>
                        <td>
                          <Select id={'ca-dd' + i} size="sm" hideLabel labelText={'Điều kiện ngày ' + (i + 1)} value={d.dieuKien} onChange={(e) => patch({ dieuKien: e.target.value as typeof d.dieuKien })}>
                            <SelectItem value="" text="Theo mặc định" />
                            {DIEU_KIEN.map(([v, t]) => <SelectItem key={v} value={v} text={t} />)}
                          </Select>
                        </td>
                        <td>{(d.dieuKien || form.dieuKien) !== 'khong'
                          ? <TextInput id={'ca-dm' + i} size="sm" hideLabel labelText={'Mức ngày ' + (i + 1)} placeholder={form.mucNgay || 'nhập mức'} value={d.muc} onChange={(e) => patch({ muc: digits(e.target.value) })} />
                          : <span className="muted">-</span>}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              <div className="row">
                <Button size="sm" kind="ghost" onClick={() => set({ ngay: form.ngay.map((d) => ({ ...d, thuong: form.ngay[0]?.thuong ?? '' })) })}>Thưởng ngày 1 cho mọi ngày</Button>
              </div>
              <div className="row">
                <Select id="ca-du" labelText="Lỡ một ngày" value={form.dutChuoi} onChange={(e) => set({ dutChuoi: e.target.value as QuestForm['dutChuoi'] })}>
                  <SelectItem value="lamLai" text="Làm lại từ ngày 1" />
                  <SelectItem value="noiTiep" text="Vẫn nối tiếp ngày kế" />
                </Select>
                <Select id="ca-hv" labelText="Nhận xong ngày cuối" value={form.hetVong} onChange={(e) => set({ hetVong: e.target.value as QuestForm['hetVong'] })}>
                  <SelectItem value="lapLai" text="Bắt đầu lại từ ngày 1" />
                  <SelectItem value="dung" text="Dừng, không nhận thêm" />
                </Select>
              </div>
              {openCheckin(form) ? (
                <InlineNotification kind="warning" lowContrast hideCloseButton title="Có ngày không cần điều kiện."
                  subtitle="Ai cũng nhận được, kể cả tài khoản lập ra chỉ để lấy thưởng. Nên chọn đối tượng 'Đã nạp tiền' hoặc đặt điều kiện cược / nạp." />
              ) : null}
            </>
          ) : (
            <>
              <Select id="ca-c" labelText="Chu kỳ" value={form.chuKy} disabled={!!editing && editing.claims > 0} onChange={(e) => set({ chuKy: e.target.value })}>
                {CHU_KY.map(([v, t]) => <SelectItem key={v} value={v} text={t} />)}
              </Select>
              {form.loai === 'nap' ? (
                <div className="row">
                  <Select id="ca-nc" labelText="Tính theo" value={form.napCach} onChange={(e) => set({ napCach: e.target.value as QuestForm['napCach'] })}>
                    <SelectItem value="tong" text="Tổng tiền nạp" />
                    <SelectItem value="lan" text="Số lần nạp" />
                  </Select>
                  <TextInput id="ca-nt" labelText={`Mỗi lần nạp tối thiểu (${CURRENCY.symbol})`} helperText="Lần nạp nhỏ hơn không tính. Để trống = mọi lần." value={form.napToiThieu}
                    onChange={(e) => set({ napToiThieu: digits(e.target.value) })} />
                </div>
              ) : null}
              {form.loai === 'van' || form.loai === 'thang' ? (
                <TextInput id="ca-ct" labelText={`Cược tối thiểu mỗi ván (${CURRENCY.symbol})`}
                  helperText={(form.loai === 'thang' ? 'Ván thắng = tiền trả về lớn hơn tiền cược. ' : '') + 'Chỉ ván chơi bằng tiền thật (không tính xu), ván hoàn cả không tính.'}
                  value={form.cuocToiThieu} onChange={(e) => set({ cuocToiThieu: digits(e.target.value) })} />
              ) : null}
              {form.loai !== 'nap' ? (
                <FilterableMultiSelect
                  key={(editing?.id ?? 'new') + form.loai + (games.data ? 'y' : 'n')}
                  id="ca-g" titleText="Game được tính" placeholder="Để trống = mọi game"
                  items={(form.loai === 'cuoc' ? games.data?.ledger : games.data?.rounds) ?? []}
                  itemToString={(i: Opt | null) => (i ? i.label : '')}
                  initialSelectedItems={((form.loai === 'cuoc' ? games.data?.ledger : games.data?.rounds) ?? []).filter((i) => form.games.includes(i.value))}
                  onChange={({ selectedItems }: { selectedItems: Opt[] | null }) => set({ games: (selectedItems ?? []).map((i) => i.value) })}
                />
              ) : null}
              <p className="cds--label">Mốc thưởng: đạt mốc nào nhận mốc đó, mỗi mốc một lần mỗi kỳ.</p>
              {form.bac.map((t, i) => {
                const u = unitOf(form.loai, form.napCach);
                const patch = (x: Partial<typeof t>) => set({ bac: form.bac.map((y, j) => (j === i ? { ...y, ...x } : y)) });
                return (
                  <div className="row" key={i}>
                    <TextInput id={'ca-bm' + i} disabled={!!editing && editing.claims > 0} labelText={`Mốc ${i + 1}: ${u.label.toLowerCase()}${u.money ? ` (${CURRENCY.symbol})` : ''}`} value={t.muc} onChange={(e) => patch({ muc: digits(e.target.value) })} />
                    <TextInput id={'ca-bt' + i} labelText={`Thưởng (${CURRENCY.symbol})`} value={t.thuong} onChange={(e) => patch({ thuong: digits(e.target.value) })} />
                    {form.bac.length > 1 && !(editing && editing.claims > 0) ? <Button size="sm" kind="ghost" onClick={() => set({ bac: form.bac.filter((_, j) => j !== i) })}>Bỏ mốc</Button> : null}
                  </div>
                );
              })}
              {form.bac.length < 10 && !(editing && editing.claims > 0) ? <div><Button size="sm" kind="tertiary" onClick={() => set({ bac: [...form.bac, { muc: '', thuong: '' }] })}>Thêm mốc</Button></div> : null}
            </>
          )}

          <p className="cds--label">Đối tượng (để trống = mọi người chơi)</p>
          <div className="row">
            <Select id="ca-dn" labelText="Nạp tiền" value={form.deposited} onChange={(e) => set({ deposited: e.target.value as QuestForm['deposited'] })}>
              <SelectItem value="" text="Không yêu cầu" />
              <SelectItem value="yes" text="Chỉ người đã nạp tiền" />
              <SelectItem value="no" text="Chỉ người chưa nạp" />
            </Select>
            <TextInput id="ca-nd" labelText="Tài khoản mới trong (ngày)" value={form.newDays} onChange={(e) => set({ newDays: digits(e.target.value).slice(0, 4) })} />
            <TextInput id="ca-vp" labelText="Điểm VIP tối thiểu" value={form.vipMin} onChange={(e) => set({ vipMin: digits(e.target.value) })} />
          </div>
          {form.userIds.length ? <p className="note">Chỉ {form.userIds.length} người chơi được chọn riêng mới thấy nhiệm vụ này (giữ nguyên khi lưu).</p> : null}
          <TextInput id="ca-vc" labelText="Vòng cược của tiền thưởng (lần)" helperText="Tiền thưởng phải cược lại bấy nhiêu lần mới rút được. Để trống = theo cài đặt chung của sự kiện."
            value={form.vongCuoc} onChange={(e) => set({ vongCuoc: digits(e.target.value).slice(0, 3) })} />
          <TextInput id="ca-bud" labelText={`Ngân sách tổng (${CURRENCY.symbol})`} helperText="Hết ngân sách thì người chơi không nhận thêm được. Để trống = không giới hạn." value={budget} onChange={(e) => setBudget(e.target.value.replace(/\D/g, ''))} />
          <TextInput id="ca-gl" labelText="Mã game ở sảnh" helperText="Ví dụ vgmn_100. Nút trong nhiệm vụ mở game này." value={form.gameLobby} onChange={(e) => set({ gameLobby: e.target.value })} />
          {can('crm.manage') || seg ? <SegmentSelect id="ca-seg" value={seg} onChange={setSeg} helperText="Chỉ người đang thuộc phân khúc thấy và nhận được. Để trống = mọi người chơi." /> : null}
          <TextInput id="ca-mc" labelText="Mã trang sự kiện của client (tùy chọn)" helperText="Chỉ khi dùng một trang sự kiện có sẵn trong game, ví dụ baotaixiu260." value={form.maClient} onChange={(e) => set({ maClient: e.target.value })} />
          {editing ? null : (
            <TextInput id="ca-h" labelText="Nhóm đối chứng (%)" inputMode="numeric"
              helperText="0 đến 50. Bấy nhiêu phần trăm người chơi (chọn cố định theo mã) không thấy chiến dịch, để so hiệu quả. Không đổi được sau khi tạo."
              invalid={Number(holdout) > 50} invalidText="Tối đa 50%" value={holdout} onChange={(e) => setHoldout(e.target.value.replace(/\D/g, '').slice(0, 2))} />
          )}
          <TextInput id="ca-s" type="datetime-local" labelText="Bắt đầu" value={startsAt} onChange={(e) => setStartsAt(e.target.value)} />
          <TextInput id="ca-e" type="datetime-local" labelText="Kết thúc" value={endsAt} onChange={(e) => setEndsAt(e.target.value)} />
          {built.error ? <p className="text-error">{built.error}</p> : null}
          <InlineError error={save.error} />
        </div>
      </Modal>
    </div>
  );
}
